/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext } from 'react';
import clsx from 'clsx';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Typography,
} from '@material-ui/core';
import ActionsIcon from '@material-ui/icons/MoreVert';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';

import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import CustomCheckbox from 'components/CustomCheckbox';
import { shortFormatDate } from '../../shared/utils/dateUtils';
import { DraftManagementContext, PAGE_SIZE_GRID } from '../../pages/draftManagement/useDraftManagement';
import { getFirstApplicantName } from '../../shared/submission/submissionPropertyAccessor';
import StatusChip from '../common/StatusChip';
import Button from '../common/Button';
import Tooltip from '../common/Tooltip';
import { usePartiesService } from '../../shared/submission/usePartiesService';
import { getSubmissionStatusId } from '../../models/SubmissionStatus';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  root: {
    padding: '1rem',
    border: `0.125rem solid ${theme.palette.white}`,
    '& :focus-visible': {
      border: `0.125rem solid ${theme.palette.cardActiveBorder}`,
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: ' repeat(auto-fit, minmax(320px, 1fr))',
    gridGap: '1rem',
  },
  showingResults: {
    textAlign: 'center',
    marginTop: '1rem',
  },
  viewMore: {
    fontWeight: 'normal',
  },
  selectedCard: {
    border: `0.125rem solid ${theme.palette.cardActiveBorder}`,
    background: theme.palette.cardActive,
  },
  userReference: {
    flex: 2,
  },
  header: {
    borderBottom: `1px solid ${theme.palette.border}`,
    padding: '0',
  },
  content: {
    display: 'inline-table',
    padding: '0.5rem',
    width: '100%',
  },
  row: {
    display: 'table-row',
  },
  label: {
    display: 'table-cell',
  },
  value: {
    display: 'table-cell',
    paddingLeft: '1rem',
    fontWeight: 'bold',
  },
}));
export default function DraftGrid() {
  const {
    data,
    rows,
    handleActionMenuClick,
    getActionsTooltipText,
    isRowSelected,
    handleSelectRow,
    pageSize,
    loadMore,
    loadLess,
    totalCount,
    loading,
    isAddressBookPage,
    computeActionEntries,
  } = useContext(DraftManagementContext);

  const { t } = useTranslation();
  const classes = useStyles();
  const { isPartyCompany } = usePartiesService();

  return loading ? (
    <CircularProgress />
  ) : (
    <>
      <div className={classes.grid}>
        {rows.map((submission) => {
          const firstApplicantName = getFirstApplicantName(
            data.filter(d => d.id === submission.id),
            isPartyCompany,
            t,
          );

          const actionEntries = computeActionEntries(submission);

          return (
            <Card
              key={submission.id}
              className={clsx(classes.root, isRowSelected(submission) ? classes.selectedCard : null)}
              elevation={1}
              component="article"
            >
              <CardHeader
                className={classes.header}
                avatar={(
                  <CustomCheckbox
                    inputProps={
                      !isRowSelected(submission)
                        ? { 'aria-label': t('actions.submission.row') }
                        : { 'aria-label': t('actions.submission.row.selected') }
                    }
                    checked={isRowSelected(submission)}
                    onChange={handleSelectRow(submission)}
                  />
                )}
                title={(
                  <Box display="flex">
                    <Typography className={classes.userReference} nowrap="true" component="span">
                      {!isAddressBookPage ? submission['basicFilingInfo.userReference'] : submission.partyType}
                    </Typography>
                    {!isAddressBookPage && (
                      <StatusChip status={getSubmissionStatusId(submission?.['filter.statusOrder'])} />
                    )}
                  </Box>
                )}
                action={(
                  <CardActions>
                    <Tooltip title={getActionsTooltipText()} disableHoverListener={actionEntries.length > 0}>
                      <span>
                        <IconButton
                          aria-label={t('actions.more')}
                          disabled={actionEntries.length <= 0}
                          onClick={handleActionMenuClick(submission)}
                          color="primary"
                        >
                          <ActionsIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                )}
              />

              <CardContent className={classes.content}>
                <Box className={classes.row}>
                  <Typography className={classes.label} component="span">
                    {!isAddressBookPage ? `${t('parties.applicant')}:` : `${t('name.company')}:`}
                  </Typography>
                  <Typography className={classes.value} component="span">
                    {!isAddressBookPage
                      ? firstApplicantName?.substring(0, 20)
                      : submission.firstName?.substring(0, 20) || submission.company?.substring(0, 15)}
                  </Typography>
                </Box>
                <Box className={classes.row}>
                  <Typography className={classes.label} component="span">
                    {!isAddressBookPage ? `${t('Title')}:` : `${t('country.place-of-business')}`}
                  </Typography>
                  <Typography className={classes.value} component="span">
                    {!isAddressBookPage
                      ? submission['basicFilingInfo.titleOfInvention']
                      : submission.country?.substring(0, 20) || submission.placeOfBusiness?.substring(0, 20)}
                  </Typography>
                </Box>

                {!isAddressBookPage && (
                  <Box className={classes.row}>
                    <Typography className={classes.label} component="span">
                      {`${t('basic-filing.last-modified.sort')}:`}
                    </Typography>
                    <Typography className={classes.value} component="span">
                      {shortFormatDate(new Date(Date.parse(submission.modifiedDate)))}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {totalCount > 0 && (
        <Typography className={classes.showingResults}>
          {t('pagination.showing.results', {
            startElement: 1,
            endElement: Math.min(pageSize, totalCount),
            totalElements: totalCount,
          })}
        </Typography>
      )}
      {totalCount > PAGE_SIZE_GRID && (
        <Box display="flex" justifyContent="center" className={classes.viewMore}>
          <Button
            variant="text"
            color="primary"
            onClick={totalCount > pageSize ? () => loadMore(Math.min(totalCount - pageSize, 5)) : () => loadLess()}
          >
            {totalCount > pageSize ? (
              <>
                {t('pagination.view.more', {
                  x: Math.min(totalCount - pageSize, 5),
                })}
                <ExpandMore />
              </>
            ) : (
              <>
                {t('pagination.view.less')}
                <ExpandLess />
              </>
            )}
          </Button>
        </Box>
      )}
    </>
  );
}
